/* eslint-disable react/no-unescaped-entities */
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowDownRight, ArrowUpRight, Menu, X, Mail, Phone, Instagram, Linkedin } from "lucide-react";
import { portfolioImages, contact } from "./data/portfolio";
import { Reveal, MaskLines, ParallaxImage } from "./components/Motion";
import { Logo } from "./components/Logo";
import { Marquee } from "./components/Marquee";
import { useSmoothScroll } from "./hooks/useSmoothScroll";
import "@/App.css";

const navItems = [["work", "Work"], ["about", "About"], ["experience", "Experience"], ["contact", "Contact"]];
const filters = ["all", "architecture", "events", "nature", "sports", "product"];
const marqueeItems = ["Architecture", "Concerts", "Nature", "Sports", "Product", "Light", "Composition"];

function Placeholder({ photo, className = "", reveal = false, delay = 0 }) {
  const [loaded, setLoaded] = useState(false);
  const motionProps = reveal
    ? {
        initial: { opacity: 0, y: 44, scale: 0.98 },
        whileInView: { opacity: 1, y: 0, scale: 1 },
        viewport: { once: true, margin: "-8% 0px" },
        transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
      }
    : {};
  const Tag = reveal ? motion.div : "div";
  return (
    <Tag className={`photo-placeholder ${className}`} data-testid={`image-${photo.filename}`} style={{ aspectRatio: `${photo.w} / ${photo.h}` }} {...motionProps}>
      <div className="placeholder-art"><span className="placeholder-mark">+</span><span>{photo.filename}</span></div>
      <img src={photo.src} alt={photo.alt} loading="lazy" onLoad={() => setLoaded(true)} className={loaded ? "is-loaded" : ""} />
      <div className="photo-caption"><span>{photo.label}</span><ArrowUpRight size={16} /></div>
    </Tag>
  );
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [activeStep, setActiveStep] = useState(0);
  const processRef = useRef(null);
  const scrollTo = useSmoothScroll();

  useEffect(() => {
    const onScroll = () => {
      const section = processRef.current;
      if (!section) return;
      const rect = section.getBoundingClientRect();
      const trigger = window.innerHeight * 0.58;
      const progress = (trigger - rect.top) / rect.height;
      setActiveStep(Math.max(0, Math.min(3, Math.floor(progress * 4))));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => { scrollTo(id); setMenuOpen(false); };
  const allPhotos = (() => {
    const groups = [portfolioImages.architecture, portfolioImages.events, portfolioImages.nature, portfolioImages.sports, portfolioImages.product];
    const max = Math.max(...groups.map((g) => g.length));
    const out = [];
    for (let i = 0; i < max; i++) groups.forEach((g) => { if (g[i]) out.push(g[i]); });
    return out;
  })();
  const gallery = filter === "all" ? allPhotos : (portfolioImages[filter] || allPhotos);
  const categories = [
    ["01", "ARCHITECTURE", "Lines, heritage, materials and the character of built spaces.", portfolioImages.architecture.slice(0, 2)],
    ["02", "CONCERTS / EVENTS", "Atmosphere, movement and moments as they happen.", portfolioImages.events.slice(0, 2)],
    ["03", "NATURE", "Landscape, natural light and quiet observations.", portfolioImages.nature.slice(0, 2)],
    ["04", "SPORTS", "Timing, energy and the decisive frame.", portfolioImages.sports.slice(0, 2)],
    ["05", "PRODUCT", "Shape, texture and considered detail.", portfolioImages.product.slice(0, 2)],
  ];
  const steps = [
    ["Observe", "Understand the environment, available light and the story within it."],
    ["Compose", "Find the perspective, proportions and framing that communicate the strongest view."],
    ["Capture", "Work with light, timing and exposure to create a clean, intentional image."],
    ["Refine", "Edit carefully while keeping the image natural, accurate and visually appealing."],
  ];

  return <main>
    <nav className="nav" data-testid="site-navigation">
      <button className="logo" data-testid="logo-link" onClick={() => go("top")}><Logo /></button>
      <div className={`nav-links ${menuOpen ? "open" : ""}`}>{navItems.map(([id, label]) => <button key={id} data-testid={`nav-${id}-link`} onClick={() => go(id)}>{label}</button>)}</div>
      <button className="menu-toggle" data-testid="mobile-menu-button" onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu">{menuOpen ? <X /> : <Menu />}</button>
    </nav>

    <section className="hero" id="top" data-testid="hero-section">
      <div className="hero-image"><Placeholder photo={portfolioImages.hero} /></div>
      <div className="hero-overlay" />
      <div className="hero-content">
        <p className="eyebrow hero-eyebrow">Photographer / India</p>
        <MaskLines testId="hero-heading" lines={["Capturing", <><em>the world</em> with</>, "intention."]} />
        <Reveal as="p" className="hero-kicker" delay={0.9}>Architecture <i>•</i> Concerts / Events <i>•</i> Nature <i>•</i> Sports <i>•</i> Product</Reveal>
        <Reveal as="p" className="hero-intro" delay={1.0}>Photographer drawn to the relationship between light, place, people and detail — building a visual practice across different environments.</Reveal>
        <Reveal as="div" className="hero-actions" delay={1.1}>
          <button className="button button-light" data-testid="view-work-button" onClick={() => go("work")}>View my work <ArrowDownRight size={17} /></button>
          <button className="text-link" data-testid="hero-contact-button" onClick={() => go("contact")}>Get in touch <ArrowUpRight size={16} /></button>
        </Reveal>
      </div>
      <div className="hero-footer"><span>01 — 09</span><span className="scroll-cue">Scroll to explore <ArrowDownRight size={16} /></span></div>
    </section>

    <Marquee items={marqueeItems} />

    <section className="section work-section" id="work" data-testid="work-section">
      <div className="section-intro">
        <Reveal as="p" className="eyebrow">01 / Selected work</Reveal>
        <Reveal as="h2" delay={0.05}>Many subjects,<br /><em>seen</em> differently.</Reveal>
        <Reveal as="p" delay={0.1}>Architecture, concerts, nature, sports and product — each frame looks for structure, light and the detail that makes an image feel considered and alive.</Reveal>
      </div>
      <div className="filter-bar" data-testid="gallery-filter">{filters.map((item) => <button key={item} className={filter === item ? "active" : ""} data-testid={`filter-${item}-button`} onClick={() => setFilter(item)}>{item}</button>)}</div>
      <div className="gallery" data-testid="portfolio-gallery">{gallery.map((photo, i) => <Placeholder key={photo.filename} photo={photo} reveal delay={(i % 3) * 0.06} className="gallery-item" />)}</div>
    </section>

    <section className="philosophy" data-testid="philosophy-section">
      <div className="section-bg" style={{ backgroundImage: `url(${portfolioImages.backgrounds.philosophy})` }} />
      <div className="blueprint-lines" />
      <Reveal as="p" className="eyebrow">02 / The point of view</Reveal>
      <Reveal as="h2" delay={0.05}>I don't just<br />record a moment.<br /><em>I photograph<br />how it feels.</em></Reveal>
      <Reveal as="p" className="philosophy-copy" delay={0.1}>Good photography is not only about having the right equipment. It is about knowing where to stand, understanding light, controlling perspective and recognizing the details that make an image work.</Reveal>
    </section>

    <section className="section beyond" data-testid="beyond-section">
      <div className="section-intro compact">
        <Reveal as="p" className="eyebrow">03 / Creative range</Reveal>
        <Reveal as="h2" delay={0.05}>More than<br /><em>one subject.</em></Reveal>
        <Reveal as="p" delay={0.1}>Photography has taken me through very different environments. Each one has taught me something about light, timing, composition and people.</Reveal>
      </div>
      <div className="category-grid">{categories.map(([num, title, desc, photos]) => <Reveal as="article" className="category" key={title} data-testid={`category-${title.toLowerCase().replaceAll(" ", "-")}`}><div className="category-head"><span>{num}</span><h3>{title}</h3></div><p>{desc}</p><div className="category-images">{photos.map((photo) => <Placeholder key={photo.filename} photo={photo} />)}</div></Reveal>)}</div>
    </section>

    <section className="section experience" id="experience" data-testid="experience-section">
      <div className="experience-heading">
        <div>
          <Reveal as="p" className="eyebrow">04 / Experience</Reveal>
          <Reveal as="h2" delay={0.05}>The work<br /><em>behind</em> the frame.</Reveal>
          <Reveal as="p" className="experience-lead" delay={0.1}>A year leading a campus photography community, alongside ongoing client assignments — each role shaping how I observe, frame and shoot.</Reveal>
        </div>
        <Reveal className="experience-foot" delay={0.15}>
          <span className="experience-stat"><strong>01</strong>year as President</span>
          <span className="experience-stat"><strong>05</strong>disciplines shot</span>
          <span className="experience-stat"><strong>∞</strong>frames made</span>
        </Reveal>
      </div>
      <div className="timeline">
        <Reveal className="timeline-item"><span className="timeline-number">01</span><div><p className="eyebrow">President / PDEU, Gandhinagar / June 2024 — May 2025</p><h3>Photography Club</h3><p>Led the university photography community for one year, organized photography activities and encouraged members to develop their approach to composition, visual storytelling and photography.</p><div className="meta-tags"><span>Team leadership</span><span>Visual storytelling</span></div></div></Reveal>
        <Reveal className="timeline-item" delay={0.08}><span className="timeline-number">02</span><div><p className="eyebrow">Paid photography assignments / Ongoing</p><h3>Freelance Photographer</h3><p>Completed paid photography assignments across events, portraits, lifestyle and commissioned shoots, working with clients to understand requirements and deliver polished final images.</p><div className="meta-tags"><span>Client work</span><span>Creative direction</span></div></div></Reveal>
      </div>
    </section>

    <section className="section about" id="about" data-testid="about-section">
      <div className="about-image"><Placeholder photo={portfolioImages.profile} reveal /></div>
      <div className="about-copy">
        <Reveal as="p" className="eyebrow">05 / About me</Reveal>
        <Reveal as="h2" delay={0.05}>Curious about<br />people, places<br /><em>and perspective.</em></Reveal>
        <Reveal as="p" delay={0.1}>I'm a photographer interested in the relationship between light, space and people.</Reveal>
        <Reveal as="p" delay={0.13}>My work moves across architecture, concerts, nature, sports and products, each offering a different way of thinking about composition and storytelling.</Reveal>
        <Reveal as="p" delay={0.16}>Across every subject, I am looking for the same thing: a clear point of view, honest light and details worth remembering.</Reveal>
        <div className="skills">{["Composition", "Natural light", "Architectural perspective", "Visual storytelling", "Photo editing", "Color & exposure", "Timing", "Attention to detail"].map((skill) => <span key={skill}>{skill}</span>)}</div>
      </div>
    </section>

    <section className="process" ref={processRef} data-testid="process-section">
      <div className="process-top"><p className="eyebrow">06 / How I see a shot</p><p className="process-note">A considered process makes room for instinct.</p></div>
      <div className="process-steps">{steps.map(([title, desc], i) => <div className={`process-step ${activeStep === i ? "active" : ""}`} key={title} data-testid={`process-step-${title.toLowerCase()}`}><span>0{i + 1}</span><h3>{title}</h3><p>{desc}</p></div>)}</div>
    </section>

    <section className="feature-image" data-testid="feature-image-section">
      <ParallaxImage photo={{ src: portfolioImages.backgrounds.feature, filename: "feature-bg", alt: "Photograph by Rishi Thakor" }} />
      <div className="feature-copy">
        <Reveal as="p" className="eyebrow">07 / Perspective</Reveal>
        <Reveal as="h2" delay={0.05}>Every subject has<br />a perspective<br /><em>worth finding.</em></Reveal>
      </div>
    </section>

    <section className="why section" data-testid="why-photography-section">
      <div>
        <Reveal as="p" className="eyebrow">08 / The direction</Reveal>
        <Reveal as="h2" delay={0.05}>Why<br /><em>photography?</em></Reveal>
      </div>
      <div className="why-copy">
        <Reveal as="p">Photography gives me a way to slow down and pay attention — to structure, gesture, atmosphere, texture and the small decisions that make a frame work.</Reveal>
        <Reveal as="p" delay={0.08}>I want to keep building a visual practice that can move comfortably between subjects while staying thoughtful, accurate and personal.</Reveal>
        <button className="text-link" data-testid="why-contact-button" onClick={() => go("contact")}>Start a conversation <ArrowUpRight size={16} /></button>
      </div>
    </section>

    <section className="contact" id="contact" data-testid="contact-section">
      <div className="section-bg" style={{ backgroundImage: `url(${portfolioImages.backgrounds.contact})` }} />
      <div className="contact-heading">
        <Reveal as="p" className="eyebrow">09 / Contact</Reveal>
        <Reveal as="h2" delay={0.05}>Let's create<br />images people<br /><em>want to remember.</em></Reveal>
        <Reveal as="p" delay={0.1}>Available for photography opportunities, commissioned work and creative collaborations.</Reveal>
        <a className="button button-light" data-testid="contact-me-button" href={`mailto:${contact.email}`}>Contact me <ArrowUpRight size={17} /></a>
      </div>
      <div className="contact-details">
        <a data-testid="contact-email" href={`mailto:${contact.email}`}><Mail size={17} /><span><small>Email</small>{contact.email}</span></a>
        <a data-testid="contact-phone" href={`tel:${contact.phone}`}><Phone size={17} /><span><small>Phone</small>{contact.phone}</span></a>
        <a data-testid="contact-instagram" href="https://instagram.com/therishithakor" target="_blank" rel="noreferrer"><Instagram size={17} /><span><small>Instagram</small>{contact.instagram}</span></a>
        <a data-testid="contact-linkedin" href="https://linkedin.com/in/therishithakor" target="_blank" rel="noreferrer"><Linkedin size={17} /><span><small>LinkedIn</small>{contact.linkedin}</span></a>
      </div>
    </section>

    <footer data-testid="site-footer"><div><strong>RISHI THAKOR</strong><span>PHOTOGRAPHER</span></div><div>Based in India<br />Available for opportunities</div><div>© 2026 Rishi Thakor</div><button data-testid="back-to-top-button" onClick={() => go("top")}>Back to top <ArrowUpRight size={15} /></button></footer>
  </main>;
}
export default App;
